<script>
import * as m from "@app/paraglide/messages.js";
import { TEAM_ROLES, roleLabel, roleDescription, initials, displayName } from "./teamApi.js";

/** @type {{ draft: any, groups: any[], isOwner: boolean, isSelf: boolean, saving: boolean, onsave: () => void, oncancel: () => void, onremove: () => void }} */
let { draft = $bindable(), groups, isOwner, isSelf, saving, onsave, oncancel, onremove } = $props();

function toggleGroup(groupId) {
    const current = draft.groups || [];
    draft.groups = current.includes(groupId) ? current.filter((id) => id !== groupId) : [...current, groupId];
}

function handleKeydown(e) {
    if (e.key === "Escape") {
        e.stopPropagation();
        oncancel();
    }
}
</script>

<!-- svelte-ignore a11y_no_noninteractive_element_interactions -->
<aside class="team-panel" aria-labelledby="memberPanelTitle" onkeydown={handleKeydown}>
    <div class="team-panel__header">
        <span class="team-avatar team-avatar--lg" aria-hidden="true">{initials(draft)}</span>
        <div class="min-w-0">
            <h5 class="mb-0 text-truncate" id="memberPanelTitle">
                {draft.isNew ? m.team_manager_add_member_title() : displayName(draft)}
            </h5>
            {#if !draft.isNew}
                <div class="small text-body-secondary text-truncate">{draft.email}</div>
            {/if}
        </div>
        <button type="button" class="btn-close ms-auto" aria-label={m.close()} onclick={oncancel}></button>
    </div>

    <form class="team-panel__body" onsubmit={(e) => { e.preventDefault(); onsave(); }}>
        <div class="row g-2 mb-3">
            <div class="col-6">
                <label class="form-label small fw-semibold" for="memberFirstName">{m.team_manager_first_name_label()}</label>
                <input id="memberFirstName" class="form-control form-control-sm" bind:value={draft.firstName} autocomplete="off" />
            </div>
            <div class="col-6">
                <label class="form-label small fw-semibold" for="memberLastName">{m.team_manager_last_name_label()}</label>
                <input id="memberLastName" class="form-control form-control-sm" bind:value={draft.lastName} autocomplete="off" />
            </div>
        </div>

        <div class="mb-3">
            <label class="form-label small fw-semibold" for="memberEmail">{m.team_manager_email_label()}</label>
            <input
                id="memberEmail"
                type="email"
                class="form-control form-control-sm"
                bind:value={draft.email}
                disabled={!draft.isNew}
                required
                autocomplete="off" />
            {#if !draft.isNew}
                <div class="form-text">{m.team_manager_email_locked_help()}</div>
            {/if}
        </div>

        <fieldset class="mb-3" disabled={isOwner}>
            <legend class="form-label small fw-semibold mb-1">{m.team_manager_user_role_label()}</legend>
            <div class="role-options">
                {#each TEAM_ROLES as role (role)}
                    <label class="role-option {draft.role === role ? 'active' : ''}">
                        <input type="radio" class="visually-hidden" name="memberRole" value={role} bind:group={draft.role} />
                        <span class="fw-semibold">{roleLabel(role)}</span>
                        <span class="small text-body-secondary">{roleDescription(role)}</span>
                    </label>
                {/each}
            </div>
            {#if isOwner}
                <div class="form-text"><i class="bi bi-lock-fill me-1"></i>{m.team_manager_owner_locked_help()}</div>
            {/if}
        </fieldset>

        <div class="mb-3">
            <div class="form-label small fw-semibold mb-1">{m.team_manager_groups_label()}</div>
            {#if groups.length === 0}
                <div class="small text-body-secondary">{m.team_manager_no_groups_available()}</div>
            {:else}
                <div class="d-flex flex-wrap gap-1">
                    {#each groups as group (group.id)}
                        {@const inGroup = (draft.groups || []).includes(group.id)}
                        <button
                            type="button"
                            class="btn btn-sm rounded-pill {inGroup ? 'btn-primary' : 'btn-outline-secondary'}"
                            aria-pressed={inGroup}
                            onclick={() => toggleGroup(group.id)}>
                            <i class="bi {inGroup ? 'bi-check-lg' : 'bi-plus-lg'} me-1"></i>{group.name || m.team_manager_unnamed_group()}
                        </button>
                    {/each}
                </div>
            {/if}
        </div>

        <div class="form-check form-switch mb-4">
            <input
                class="form-check-input"
                type="checkbox"
                role="switch"
                id="memberDisabled"
                bind:checked={draft.disable}
                disabled={isOwner || isSelf} />
            <label class="form-check-label" for="memberDisabled">{m.team_manager_disable_account_label()}</label>
            <div class="form-text mt-0">{m.team_manager_disable_account_help()}</div>
        </div>

        <div class="d-flex gap-2">
            <button type="submit" class="btn btn-primary btn-sm flex-grow-1" disabled={saving}>
                {#if saving}<span class="spinner-border spinner-border-sm me-1" aria-hidden="true"></span>{/if}
                {draft.isNew ? m.team_manager_add_member_button() : m.team_manager_save_changes_button()}
            </button>
            <button type="button" class="btn btn-outline-secondary btn-sm" onclick={oncancel} disabled={saving}>{m.cancel()}</button>
        </div>

        {#if !draft.isNew && !isOwner && !isSelf}
            <div class="danger-zone mt-4">
                <div class="small fw-semibold text-danger mb-1">{m.team_manager_danger_zone()}</div>
                <button type="button" class="btn btn-outline-danger btn-sm w-100" onclick={onremove} disabled={saving}>
                    <i class="bi bi-person-dash me-1"></i>{m.team_manager_remove_member_button()}
                </button>
            </div>
        {/if}
    </form>
</aside>

<style>
    .team-panel {
        display: flex;
        flex-direction: column;
        border: 1px solid var(--bs-border-color);
        border-radius: 0.5rem;
        background: var(--bs-body-bg);
        min-height: 0;
        overflow: hidden;
    }

    .team-panel__header {
        display: flex;
        align-items: center;
        gap: 0.75rem;
        padding: 0.875rem 1rem;
        border-bottom: 1px solid var(--bs-border-color);
        background: var(--bs-tertiary-bg);
    }

    .team-panel__body {
        padding: 1rem;
        overflow-y: auto;
    }

    .min-w-0 {
        min-width: 0;
    }

    .role-options {
        display: grid;
        grid-template-columns: 1fr 1fr;
        gap: 0.375rem;
    }

    .role-option {
        display: flex;
        flex-direction: column;
        gap: 0.125rem;
        padding: 0.5rem 0.625rem;
        border: 1px solid var(--bs-border-color);
        border-radius: 0.375rem;
        cursor: pointer;
        line-height: 1.25;
        transition: border-color 0.15s ease-in-out, background-color 0.15s ease-in-out;
    }

    .role-option:hover {
        border-color: var(--bs-primary);
    }

    .role-option.active {
        border-color: var(--bs-primary);
        background: var(--bs-primary-bg-subtle);
    }

    .role-option:has(input:focus-visible) {
        outline: 2px solid var(--bs-primary);
        outline-offset: 1px;
    }

    fieldset:disabled .role-option {
        cursor: not-allowed;
        opacity: 0.65;
    }

    .danger-zone {
        padding-top: 1rem;
        border-top: 1px dashed var(--bs-border-color);
    }
</style>
